// LIBRARIES
import React, { useEffect, useState } from "react";
import { useT } from "@transifex/react";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { useBookContext } from "../../../context/BookContext";
import { useHistory } from "react-router-dom";
import StyledButton from "../../../basic/styledButton/StyledButton";
import ResponsiveButton from "../../../basic/responsiveButton/ResponsiveButton";
import SelectSearch, { fuzzySearch } from "react-select-search";
import { P6uBand, P6uPublisherBook } from "p6m-p6u";
import CtasWrapper from "../../../wrapper/create/ctasWrapper/CtasWrapper";
import { useGeneralContext } from "../../../context/AppGeneralContext";
import { useLogger } from "../../../hooks/useLogger";
import { useConfirmBook } from "../../../hooks/useConfirmBook";
import { CreationModes } from "../../../constants/CreationFlowConstants";
import AccordionTab from "../../../basic/accordionTab/AccordionTab";
import SelectBookDropdownsRT from "../selectBookDropdownsRT/SelectBookDropdownsRT";
import ExerciseSheetTitle from "../../../basic/create/ExerciseSheetTitle/ExerciseSheetTitle";
import CreationFlowSection from "../../../basic/create/creationFlowSection/CreationFlowSection";
import BookSelectionBanners from "./bookSelectionBanners/BookSelectionBanners";
import useBookDropdownOptions from "./useBookDropdownOptions";
import { StickyWrapper } from "../../../basic/accordionTab/StyledComponents";
import VocabTrainingCreationHeader from "../../../complex/create/vocabTrainingCreationHeader/VocabTrainingCreationHeader";
import TestCreationHeader from "../../../complex/create/testCreationHeader/TestCreationHeader";

import {
    Wrapper,
    MiddleContentWrapper,
    SelectsWrapper,
    SelectBookBtn,
    SmallSelectWrapper,
    BookSearchSelectWrapper,
    BookInfoWrapper,
    BookImageWrapper,
    BookNameWrapper,
    BookName,
    SelectedContentWrapper,
    StyledCoverImage,
    TestToText,
} from "./StyledComponents";

/** The book selection before the redesign: the "dropdowns" variant of the book selection experiment. */
const SelectBookDropdowns = () => {
    const { vocabularyDrawerContent, selectedBookData, testContent } = useTestCreationContext();

    const { setLastSourceSelected } = useBookContext();
    const {
        loadAvailableSubjects,
        availableSubjects,
        loadPublisherBooksForSubject,
        availablePublishersAndBooks,
        loadBandsForBook,
        availableBands,
        setAvailablePublishersAndBooks,
        setAvailableBands,
    } = useBookDropdownOptions();

    const { creatingMode, userInfo } = useGeneralContext();
    const { logEvent } = useLogger();
    const confirmBook = useConfirmBook();

    const t = useT();
    const t_selectBook = t("Select Book", { _tags: "CreateTest,SelectBook" });
    const t_back = t("Back", { _tags: "CreateTest,SelectBook" });
    const t_searchBook = t("Search Book", { _tags: "CreateTest,SelectBook" });
    const t_noResults = t("No Results", { _tags: "CreateTest,SelectBook" });
    const t_pleaseSelectLanguage = t("Please select first a language", { _tags: "CreateTest,SelectBook" });
    const t_testTo = t("Test to", { _tags: "CreateTest,SelectVocabularySource" });
    const t_selectLanguage = t("Select a Language", { _tags: "CreateTest,SelectVocabularySource" });
    const t_selectBand = t("Select a Band", { _tags: "CreateTest,SelectVocabularySource" });

    const t_ChooseVocabulary = t("Choose vocabulary:", { _tags: "CreateTest,Tab" });
    const t_WhatToPractice = t("What should be practised?", { _tags: "CreateTest,Tab" });
    const t_DesignExerciseUnit = t("Design exercise unit:", { _tags: "CreateTest,Tab" });
    const t_HowToPractice = t("How should be practised?", { _tags: "CreateTest,Tab" });
    const t_selectBookInstruction = t("Please select a book.", {
        _tags: "CreateTest,Summary",
    });

    const [selectedSubject, setSelectedSubject] = useState("");
    const [selectedPublisherBook, setSelectedPublisherBook] = useState("");
    const [selectedBand, setSelectedBand] = useState("");

    const [selectedPublisherBookInfo, setSelectedPublisherBookInfo] = useState<P6uPublisherBook>({});
    const [selectedBandInfo, setSelectedBandInfo] = useState<P6uBand>({});

    const [searchBookPlaceholder, setSearchBookPlaceholder] = useState("");
    const [selectBandPlaceholder, setSelectBandPlaceholder] = useState("");
    const [shouldInitPublisherBook, setShouldInitPublisherBook] = useState(false);
    const [shouldInitBand, setShouldInitBand] = useState(false);

    const history = useHistory();

    useEffect(() => {
        sessionStorage.removeItem("deepLinkInProgress");

        if (availableSubjects.length === 0) {
            loadAvailableSubjects();
        }

        // Init when selected a book already
        if (selectedBookData) {
            if (selectedBookData.subject?.ID) {
                setSelectedSubject(selectedBookData.subject.ID);

                if (selectedBookData.publisherBook?.CombinedID) {
                    setShouldInitPublisherBook(true);

                    if (selectedBookData.band?.UUID) {
                        setShouldInitBand(true);
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (shouldInitPublisherBook && availablePublishersAndBooks.length > 0) {
            setSelectedPublisherBook(selectedBookData.publisherBook!.CombinedID!);
            setSelectedPublisherBookInfo(selectedBookData.publisherBook!);
            setShouldInitPublisherBook(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldInitPublisherBook, availablePublishersAndBooks]);

    useEffect(() => {
        if (shouldInitBand && availableBands.length > 0) {
            setSelectedBand(selectedBookData.band!.UUID!);
            setSelectedBandInfo(selectedBookData.band!);
            setShouldInitBand(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldInitBand, availableBands]);

    useEffect(() => {
        if (selectedSubject && selectedSubject !== "") {
            loadPublisherBooksForSubject(selectedSubject);
            setSearchBookPlaceholder(t_searchBook);
        } else {
            setSearchBookPlaceholder("");
        }
        setSelectBandPlaceholder("");
        setAvailablePublishersAndBooks([]);
        setAvailableBands([]);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSubject]);

    useEffect(() => {
        if (selectedSubject && selectedPublisherBook && selectedPublisherBook !== "") {
            loadBandsForBook(selectedSubject, selectedPublisherBook);
            setSelectBandPlaceholder(t_selectBand);
        } else {
            setAvailablePublishersAndBooks([]);
            setSelectBandPlaceholder("");
        }
        setAvailableBands([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPublisherBook]);

    const updateSubject = (selectedId: any) => {
        setSelectedSubject(selectedId as string);
        setSelectedPublisherBook("");
        setSelectedBand("");
        setSelectedBandInfo({});
        setSelectedPublisherBookInfo({});
        logEvent("Subject Selected", {
            subject: availableSubjects.find((s) => s.ID === selectedId)?.Name,
            subject_id: selectedId,
        });
    };

    const updatePublisherBook = (selectedId: any) => {
        const selectedBook = availablePublishersAndBooks.find((pb) => pb.CombinedID === selectedId);
        setSelectedPublisherBook(selectedId as string);
        setSelectedBand("");
        setSelectedBandInfo({});
        setSelectedPublisherBookInfo(selectedBook ?? {});
        logEvent("Book Selected", { book_id: selectedId, publisher: selectedBook?.PublisherName });
    };

    const updateBand = (selectedId: any) => {
        const selectedBand = availableBands.find((b) => b.UUID === selectedId);
        setSelectedBand(selectedId as string);
        setSelectedBandInfo(selectedBand ?? {});
        logEvent("Band Selected", { band: selectedBand?.ShortName ?? selectedBand?.Name, band_id: selectedBand?.UUID });
    };

    function continueToVocabularySelection() {
        confirmBook({
            band: selectedBandInfo,
            publisherBook: selectedPublisherBookInfo,
            subject: availableSubjects.find((s) => s.ID === selectedSubject),
        });
    }

    function handleBackButton() {
        history.goBack();
    }

    const renderSelection = () => {
        return (
            <SelectsWrapper>
                <SmallSelectWrapper data-testid="select-subject">
                    <SelectSearch
                        options={availableSubjects.map((s) => {
                            return { name: s.Name ?? "", value: s.ID ?? "" };
                        })}
                        search
                        filterOptions={fuzzySearch}
                        emptyMessage={t_noResults}
                        placeholder={t_selectLanguage}
                        value={selectedSubject}
                        onChange={updateSubject}
                    />
                </SmallSelectWrapper>
                <BookSearchSelectWrapper data-testid="select-publisher-book">
                    <SelectSearch
                        options={availablePublishersAndBooks.map((s) => {
                            return { name: s.DisplayName ?? "", value: s.CombinedID ?? "" };
                        })}
                        search
                        filterOptions={fuzzySearch}
                        placeholder={searchBookPlaceholder}
                        value={selectedPublisherBook}
                        onChange={updatePublisherBook}
                        disabled={!selectedSubject}
                        emptyMessage={availablePublishersAndBooks.length > 0 ? t_noResults : t_pleaseSelectLanguage}
                    />
                </BookSearchSelectWrapper>
                <SmallSelectWrapper data-testid="select-band">
                    <SelectSearch
                        options={availableBands.map((s) => {
                            return { name: s.ShortName || s.Name || "", value: s.UUID ?? "" };
                        })}
                        search
                        filterOptions={fuzzySearch}
                        emptyMessage={t_noResults}
                        disabled={!selectedPublisherBook}
                        value={selectedBand}
                        onChange={updateBand}
                        placeholder={selectBandPlaceholder}
                    />
                </SmallSelectWrapper>
            </SelectsWrapper>
        );
    };

    const renderSelectedBook = () => {
        return (
            <SelectedContentWrapper>
                {selectedBandInfo && selectedBandInfo.UUID && (
                    <BookInfoWrapper>
                        <BookImageWrapper>
                            <StyledCoverImage imageId={"https://www.phase-6.de" + selectedBandInfo.Image} />
                        </BookImageWrapper>
                        <BookNameWrapper>
                            <TestToText>{t_testTo}</TestToText>
                            <BookName>
                                {selectedPublisherBookInfo.BookName} ({selectedPublisherBookInfo.PublisherName}) (
                                {selectedBandInfo.ShortName})
                            </BookName>
                        </BookNameWrapper>
                        <SelectBookBtn
                            data-testid="button-select-book"
                            icon={"library"}
                            iconColor={"white"}
                            iconPosition={"LEFT"}
                            onClick={continueToVocabularySelection}
                            arrowDirection={"RIGHT"}
                            buttonStyle={"PRIMARY"}
                        >
                            {t_selectBook}
                        </SelectBookBtn>
                    </BookInfoWrapper>
                )}
            </SelectedContentWrapper>
        );
    };

    const renderAccordionTabVocabularyContent = () => {
        return (
            <div>
                <MiddleContentWrapper>
                    {renderSelection()}
                    {renderSelectedBook()}
                </MiddleContentWrapper>
                <BookSelectionBanners />
            </div>
        );
    };

    const renderAccordionTabVocabulary = () => {
        const usedVocabulary =
            Object.values(vocabularyDrawerContent).filter((word) => word.exerciseIds && word.exerciseIds.length > 0)
                .length ?? 0;
        const availableVocabulary = Object.values(vocabularyDrawerContent).length || 0;

        return (
            <AccordionTab
                mainIconName={"vocabulary-list-unset"}
                title={t_ChooseVocabulary}
                description={t_WhatToPractice}
                counterValue={"(" + usedVocabulary + " / " + availableVocabulary + ")"}
                showAccordionTabDetails
                isActive
            >
                {t_selectBookInstruction}
                {renderAccordionTabVocabularyContent()}
            </AccordionTab>
        );
    };

    const renderAccordionTabExercises = () => {
        if (testContent.length > 0) {
            const amountExercises = testContent.length;

            return (
                <StickyWrapper>
                    <AccordionTab
                        mainIconName={"clipboard"}
                        title={t_DesignExerciseUnit}
                        description={t_HowToPractice}
                        counterValue={"(" + amountExercises + ")"}
                        onClickEdit={() => {
                            setLastSourceSelected(undefined);
                            history.push("/create/summary");
                        }}
                    >
                        <></>
                    </AccordionTab>
                </StickyWrapper>
            );
        } else {
            return <div />;
        }
    };

    if (creatingMode === CreationModes.RECURRING_TASK) {
        return (
            <VocabTrainingCreationHeader hasFullHeightContent>
                <SelectBookDropdownsRT />
            </VocabTrainingCreationHeader>
        );
    } else {
        return (
            <TestCreationHeader hasFullHeightContent>
                <Wrapper>
                    <CreationFlowSection>
                        <ExerciseSheetTitle />
                    </CreationFlowSection>
                    {renderAccordionTabVocabulary()}
                    {renderAccordionTabExercises()}
                </Wrapper>
                {!userInfo.isAnonymousUser && (
                    <CtasWrapper
                        showScrollToTop
                        isSticky
                    >
                        <ResponsiveButton
                            onClick={handleBackButton}
                            iconName="chevron-left"
                            label={t_back}
                        >
                            <StyledButton
                                onClick={handleBackButton}
                                arrowDirection={"LEFT"}
                                buttonStyle={"BLANK"}
                            >
                                {t_back}
                            </StyledButton>
                        </ResponsiveButton>
                    </CtasWrapper>
                )}
            </TestCreationHeader>
        );
    }
};

export default SelectBookDropdowns;
